import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDataGridTextFilter } from '../../../DataGrid/filters/useDataGridTextFilter.jsx';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../DataGrid/sorters/DataGridSorters.jsx';
import DataGrid from '../../../DataGrid/DataGrid.jsx';
import { Tag } from 'antd';
import CollapseBlock from '../../../blocks/CollapseBlock/CollapseBlock.jsx';
import { QuestionModal, Spinner } from '../../../index.jsx';
import { DatesListTableSettings } from './DatesTableSettings.jsx';
import { SeriousDeleteQuestion } from '../../../DeleteQuestion/SeriousDeleteQuestion.jsx';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useDataGridDateRangeFilter } from '../../../DataGrid/filters/useDataGridDateRangeFilter.jsx';
import { useOptionsDataGridFilter } from '../../../DataGrid/filters/DataGridFilters.jsx';
import { DATETIME_FORMAT_DATETIME } from '../../../../../utils/helpers/CommonHelper.jsx';
import useUserHasRole from '../../../../../hooks/useUserHasRoles.jsx';
import { ROLE_FRI_OPEN_PARTICIPANT } from '../../../../../utils/constants/roles.js';

const DatesTable = ({
  dates,
  onDelete,
  onDetailClick,
  onRowClick,
  isMyDates,
  onSignOnClick,
  onUnsignOnClick,
  onParticipantsClick,
  onEditClick,
  onCopyClick,
  onToggleEnabled,
  isArchivedParent,
}) => {
  const { t } = useTranslation();
  const locationTextFilterProps = useDataGridTextFilter('location');
  const capacityTextFilterProps = useDataGridTextFilter('capacity');
  const dateFromDateRangeFilterProps = useDataGridDateRangeFilter('date_from');
  const dateToDateRangeFilterProps = useDataGridDateRangeFilter('date_to');
  const workshopNameTextFilterProps = useDataGridTextFilter(['workshop', 'name']);
  const statusFilterProps = useOptionsDataGridFilter('is_enabled_filter', {
    1: t('Aktívny'),
    0: t('Zrušený'),
  });

  const [rowToBeDeleted, setRowToBeDeleted] = useState(null);
  const [rowToBeUnSigned, setRowToBeUnSigned] = useState(null);
  const [rowToBeCancelled, setRowToBeCancelled] = useState(null);
  const user = useSelector((state) => state.auth?.user);
  const isFriOpenParticipant = useUserHasRole(ROLE_FRI_OPEN_PARTICIPANT);
  const showEnabledStatusColumn = !isMyDates || Boolean(onToggleEnabled);

  const handleToggleEnabledClick = (date) => {
    if (!onToggleEnabled) {
      return;
    }

    const isEnabled = Boolean(date?.is_enabled ?? true);
    if (!isEnabled) {
      onToggleEnabled(date);
      return;
    }

    const participantsCount = Number(date?.participants_count ?? 0);
    if (participantsCount > 0) {
      setRowToBeCancelled(date);
      return;
    }

    onToggleEnabled(date);
  };

  const normalizedDates = useMemo(
    () =>
      (dates || []).map((date) => {
        const isEnabled = Boolean(date?.is_enabled ?? true);

        return {
          ...date,
          is_enabled_filter: isEnabled ? '1' : '0',
        };
      }),
    [dates],
  );

  const columns = useMemo(
    () => [
      ...(isMyDates
        ? [
            {
              title: t('Názov semináru'),
              dataIndex: ['workshop', 'name'],
              ellipsis: true,
              sorter: dataGridTextSorter('workshop.name'),
              ...workshopNameTextFilterProps,
            },
          ]
        : []),
      {
        title: t('Miestnosť'),
        dataIndex: 'location',
        ellipsis: true,
        width: isMyDates ? 120 : undefined,
        sorter: dataGridTextSorter('location'),
        defaultSortOrder: isMyDates ? undefined : 'ascend',
        ...locationTextFilterProps,
      },
      {
        title: t('Obsadenosť'),
        dataIndex: 'capacity',
        ellipsis: true,
        width: isMyDates ? 110 : undefined,
        sorter: (a, b) => {
          const aCurrent = Number(a.participants_count ?? 0);
          const bCurrent = Number(b.participants_count ?? 0);
          const aCapacity = Number(a.capacity ?? 0);
          const bCapacity = Number(b.capacity ?? 0);

          if (aCapacity === 0 || bCapacity === 0) {
            return aCurrent - bCurrent;
          }

          return aCurrent / aCapacity - bCurrent / bCapacity;
        },
        render: (_, record) => {
          const currentCapacity = Number(record.participants_count ?? 0);
          const capacity = Number(record.capacity ?? 0);

          return `${currentCapacity}/${capacity}`;
        },
        defaultSortOrder: isMyDates ? undefined : 'ascend',
        ...capacityTextFilterProps,
      },

      {
        title: t('Začiatok'),
        dataIndex: 'date_from',
        ellipsis: true,
        width: isMyDates ? 145 : undefined,
        sorter: dataGridDateSorter('date_from'),
        defaultSortOrder: isMyDates ? 'ascend' : undefined,
        render: (text) =>
          text ? dayjs(text).format(DATETIME_FORMAT_DATETIME) : '',
        ...dateFromDateRangeFilterProps,
      },
      {
        title: t('Koniec'),
        dataIndex: 'date_to',
        ellipsis: true,
        width: isMyDates ? 145 : undefined,
        sorter: dataGridDateSorter('date_to'),
        defaultSortOrder: isMyDates ? undefined : 'ascend',
        render: (text) =>
          text ? dayjs(text).format(DATETIME_FORMAT_DATETIME) : '',
        ...dateToDateRangeFilterProps,
      },
      ...(showEnabledStatusColumn
        ? [
            {
              title: t('Stav'),
              dataIndex: 'is_enabled_filter',
              ellipsis: true,
              width: 120,
              sorter: (a, b) =>
                Number(a?.is_enabled_filter ?? '1') -
                Number(b?.is_enabled_filter ?? '1'),
              render: (_, record) => {
                const isEnabled = (record?.is_enabled_filter ?? '1') === '1';

                return isEnabled ? (
                  <Tag color="success">{t('Aktívny')}</Tag>
                ) : (
                  <Tag color="error">{t('Zrušený')}</Tag>
                );
              },
              ...statusFilterProps,
            },
          ]
        : []),
      ...(isFriOpenParticipant
        ? [
            {
              title: t('Registrácia'),
              dataIndex: 'is_user_registered',
              ellipsis: true,
              width: isMyDates ? 125 : undefined,
              sorter: (a, b) => {
                const aRegistered = isMyDates
                  ? 1
                  : Number(Boolean(a?.is_user_registered));
                const bRegistered = isMyDates
                  ? 1
                  : Number(Boolean(b?.is_user_registered));

                return aRegistered - bRegistered;
              },
              render: (_, record) => {
                const isRegistered = isMyDates
                  ? true
                  : Boolean(record?.is_user_registered);

                return isRegistered ? (
                  <Tag color="success">{t('Prihlásený')}</Tag>
                ) : (
                  <Tag color="error">{t('Neprihlásený')}</Tag>
                );
              },
            },
          ]
        : []),
    ],
    [
      t,
      isMyDates,
      locationTextFilterProps,
      capacityTextFilterProps,
      dateFromDateRangeFilterProps,
      dateToDateRangeFilterProps,
      workshopNameTextFilterProps,
      statusFilterProps,
      isFriOpenParticipant,
      showEnabledStatusColumn,
    ],
  );

  const groupedDatesByEvent = useMemo(() => {
    if (!isMyDates) {
      return [];
    }

    const eventGroups = new Map();

    normalizedDates.forEach((date) => {
      const eventId = date?.workshop?.event?.id ?? 'unknown-event';
      const eventName = date?.workshop?.event?.name ?? t('Neznáma akcia');
      const dateFromTimestamp = dayjs(date?.date_from).isValid()
        ? dayjs(date.date_from).valueOf()
        : Number.MAX_SAFE_INTEGER;

      if (!eventGroups.has(eventId)) {
        eventGroups.set(eventId, {
          eventId,
          eventName,
          firstDateTimestamp: dateFromTimestamp,
          dates: [],
        });
      }

      const group = eventGroups.get(eventId);
      group.dates.push(date);
      group.firstDateTimestamp = Math.min(
        group.firstDateTimestamp,
        dateFromTimestamp,
      );
    });

    return Array.from(eventGroups.values())
      .map((group) => ({
        ...group,
        dates: [...group.dates].sort((a, b) => {
          const aFrom = dayjs(a?.date_from).isValid()
            ? dayjs(a.date_from).valueOf()
            : Number.MAX_SAFE_INTEGER;
          const bFrom = dayjs(b?.date_from).isValid()
            ? dayjs(b.date_from).valueOf()
            : Number.MAX_SAFE_INTEGER;

          return aFrom - bFrom;
        }),
      }))
      .sort((a, b) => {
        if (a.firstDateTimestamp !== b.firstDateTimestamp) {
          return a.firstDateTimestamp - b.firstDateTimestamp;
        }

        return a.eventName.localeCompare(b.eventName, 'sk');
      });
  }, [normalizedDates, isMyDates, t]);

  if (!dates) {
    return <Spinner />;
  }

  return (
    <>
      {isMyDates ? (
        groupedDatesByEvent.map((group) => (
          <div key={group.eventId} style={{ marginBottom: '16px' }}>
            <CollapseBlock title={group.eventName}>
              <DataGrid
                rowKey={(date) => date.id}
                columns={columns}
                dataSource={group.dates}
                onRowClick={onRowClick}
                customSettings={(row) => (
                  <DatesListTableSettings
                    date={row}
                    onEditClick={onEditClick}
                    onDetailClick={onDetailClick}
                    onDelete={
                      user && onDelete ? (row) => setRowToBeDeleted(row) : undefined
                    }
                    onCopyClick={onCopyClick}
                    onSignOnClick={onSignOnClick}
                    onUnsignOnClick={
                      onUnsignOnClick ? () => setRowToBeUnSigned(row) : undefined
                    }
                    onToggleEnabled={onToggleEnabled ? handleToggleEnabledClick : undefined}
                    onParticipantsClick={onParticipantsClick}
                    isMyDates={isMyDates}
                    isArchivedParent={isArchivedParent}
                  />
                )}
              />
            </CollapseBlock>
          </div>
        ))
      ) : (
        <DataGrid
          rowKey={(date) => date.id}
          columns={columns}
          dataSource={normalizedDates}
          onRowClick={onRowClick}
          customSettings={(row) => (
            <DatesListTableSettings
              date={row}
              onEditClick={onEditClick}
              onDetailClick={onDetailClick}
              onDelete={
                user && onDelete ? (row) => setRowToBeDeleted(row) : undefined
              }
              onCopyClick={onCopyClick}
              onSignOnClick={onSignOnClick}
              onUnsignOnClick={
                onUnsignOnClick ? () => setRowToBeUnSigned(row) : undefined
              }
              onToggleEnabled={onToggleEnabled ? handleToggleEnabledClick : undefined}
              onParticipantsClick={onParticipantsClick}
              isMyDates={isMyDates}
              isArchivedParent={isArchivedParent}
            />
          )}
        />
      )}

      <QuestionModal
        chosenPositive={() => {
          onDelete(rowToBeDeleted);
          setRowToBeDeleted(null);
        }}
        question={
          <SeriousDeleteQuestion
            firstText={t('Naozaj chcete vymazať tento termín?')}
            mainNotice={t(
              'S termínom sa odhlásia a odstránia aj VŠETCI prihlásení účastníci.',
            )}
            lastText={t(
              'Dôkladne zvážte tento krok. Operácia sa nedá vrátiť späť!',
            )}
          />
        }
        visible={rowToBeDeleted !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeleted(null)}
        headerTitle={t('Vymazať položku')}
      />

      <QuestionModal
        chosenPositive={() => {
          onUnsignOnClick(rowToBeUnSigned);
          setRowToBeUnSigned(null);
        }}
        question={t('Naozaj sa chcete odhlásiť z tohto termínu?')}
        visible={rowToBeUnSigned !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeUnSigned(null)}
        headerTitle={t('Odhlásenie sa z termínu')}
      />

      <QuestionModal
        chosenPositive={() => {
          onToggleEnabled?.(rowToBeCancelled);
          setRowToBeCancelled(null);
        }}
        question={t(
          'Pri zrušení termínu budú všetci účastníci automaticky odhlásení a bude im odoslaný informačný e-mail. Naozaj chcete zrušiť tento termín?',
        )}
        visible={rowToBeCancelled !== null}
        yes={t('Áno, zrušiť termín')}
        toggle={() => setRowToBeCancelled(null)}
        headerTitle={t('Zrušiť termín')}
      />
    </>
  );
};

DatesTable.propTypes = {
  dates: PropTypes.array,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onRowClick: PropTypes.func,
  onCreateClick: PropTypes.func,
  isAdmin: PropTypes.bool,
  isMyDates: PropTypes.bool,
  onSignOnClick: PropTypes.func,
  onUnsignOnClick: PropTypes.func,
  onParticipantsClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onCopyClick: PropTypes.func,
  onToggleEnabled: PropTypes.func,
  isArchivedParent: PropTypes.bool,
};

export default DatesTable;
